import { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  ACCOUNTS,
  LOAD_ASTRADA_ACCOUNT,
  REQUEST_BULK_ENROLLMENT,
  UPDATE_ASTRADA_CONNECTION,
} from "graphql/account";
import {
  ADD_ASTRADA_EXTERNAL_CARDS,
  GET_ASTRADA_EXTERNAL_PENDING_CARDS,
  GET_CARDS,
} from "graphql/card";
import {
  CONNECT_INTEGRATION,
  DISCONNECT_INTEGRATION,
} from "graphql/integration";
import AccountType from "enums/AccountType";
import AccountStatus from "enums/AccountStatus";
import CardStatusType from "enums/CardStatusType";
import {
  AssignedCardUser,
  AstradaCard,
  AstradaConnection,
  EnrolledCard,
} from "components/admin/linkedAccounts/types";
import { readCardEnrollment } from "components/admin/linkedAccounts/cardEnrollment";
import {
  MOCK_CARDS,
  MOCK_CONNECTIONS,
  astradaMockMode,
} from "components/admin/linkedAccounts/mockAstradaData";

// A connection is created SUBMITTED and only flips to ACTIVE once the
// networkbulkfeed.statechanged webhook lands, so it has to be queried for the Pending state
// to appear at all. See README.md.
const ASTRADA_CONNECTION_STATUSES = [
  AccountStatus.ACTIVE,
  AccountStatus.SUBMITTED,
  AccountStatus.DISCONNECTED,
  AccountStatus.REQUESTED,
];

// One page each rather than paged: a program is a few dozen cards.
const CONNECTION_PAGE_SIZE = 25;
const CARD_PAGE_SIZE = 100;

interface AccountNode {
  id: string;
  name: string | null;
  accountType: AccountType;
  url: string | null;
  key: string | null;
  status: AccountStatus;
  createdAt: number | null;
  astradaCard?: AstradaCard;
}

interface CardNode {
  id: string;
  cardNumber: string | null;
  nickname: string | null;
  status: string;
  user: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  } | null;
  account: {
    id: string;
    name: string | null;
    astradaCard: {
      network: string | null;
    };
  } | null;
  activePolicy: { card_activation_date: string | null } | null;
  cardIssuerId: string | null;
}

interface TransactionNode {
  id: string;
  transactionDate: number | null;
  merchant: string;
  amount: number;
  currency: string | null;
  user: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  card: { id: string; cardNumber: string | null } | null;
}

interface Edge<T> {
  node: T;
}

interface Connection<T> {
  edges: Edge<T>[] | null;
}

const edgeNodes = <T>(connection: Connection<T> | null | undefined): T[] =>
  (connection?.edges ?? []).map((edge) => edge.node);

const fullName = (
  person: { firstName: string | null; lastName: string | null } | null,
): string => [person?.firstName, person?.lastName].filter(Boolean).join(" ");

/**
 * Everything the Astrada Linked Accounts screen reads and writes.
 *
 * Replaced `useGetAstradaAccounts` from PR #1144, now deleted. See README.md.
 */
export function useAstradaLinkedAccounts() {
  // Read once so it cannot change between the query skips and the return below.
  const mockMode = astradaMockMode();
  const mocking = mockMode !== "off";
  const [astradaConnection, setAstradaConnection] =
    useState<AstradaConnection>();

  // Held in state so an assignment under mocks moves the card between tabs.
  const [mockCards, setMockCards] = useState<EnrolledCard[]>(MOCK_CARDS);

  const {
    data: accountsData,
    loading: connectionsLoading,
    error: connectionsError,
    refetch: refetchConnections,
  } = useQuery(ACCOUNTS, {
    variables: {
      accountType: [
        AccountType.ASTRADA_CONNECTION,
        AccountType.ASTRADA_BANK_CONNECTION,
      ],
      status: ASTRADA_CONNECTION_STATUSES,
      first: CONNECTION_PAGE_SIZE,
    },
    fetchPolicy: "cache-and-network",
    skip: mocking,
  });

  const connectionAccounts = useMemo(
    () => edgeNodes<AccountNode>(accountsData?.accounts),
    [accountsData],
  );

  const connectionIds = useMemo(
    () => new Set(connectionAccounts.map((account) => account.id)),
    [connectionAccounts],
  );

  // Scoped when there is one connection, the usual case; several fall back to one
  // unfiltered page matched client-side. See README.md.
  const soleConnectionId =
    connectionAccounts.length === 1 ? connectionAccounts[0].id : undefined;

  const {
    data: cardsData,
    loading: cardsLoading,
    refetch: refetchCards,
  } = useQuery(GET_CARDS, {
    variables: { first: CARD_PAGE_SIZE, accountId: soleConnectionId },
    fetchPolicy: "cache-and-network",
    skip: connectionAccounts.length === 0,
  });

  const cards = useMemo<EnrolledCard[]>(() => {
    return edgeNodes<CardNode>(cardsData?.cards)
      .filter((card) => card.account && connectionIds.has(card.account.id))
      .map((card) => {
        const assignedUser: AssignedCardUser | null = card.user
          ? {
              id: card.user.id,
              name: fullName(card.user),
              email: card.user.email,
            }
          : null;

        return {
          id: card.id,
          cardNumber: card.cardNumber ?? "",
          nickname: card.nickname,
          status: card.status,
          // The API does not expose the issuer's cardholder name yet. See README.md.
          cardholderName: fullName(card.user),
          assignedUser,
          enrollment: readCardEnrollment(card.status, assignedUser),
          connectionId: card.account?.id ?? "",
          connectionName: card.account?.name ?? "",
          connectionNetwork: card.account?.astradaCard?.network ?? "",
          activatedAt: card.activePolicy?.card_activation_date ?? null,
          cardIssuerId: card.cardIssuerId,
        };
      });
  }, [cardsData, connectionIds]);

  const connections = useMemo<AstradaConnection[]>(() => {
    return connectionAccounts.map((account) => ({
      id: account.id,
      name: account.name ?? "",
      status: account.status,
      networkReference: account.astradaCard?.networkReference || "",
      cardCount: cards.filter((card) => card.connectionId === account.id)
        .length,
      connectedAt: account.createdAt,
      astradaCard: account.astradaCard,
      accountType: account.accountType,
    }));
  }, [connectionAccounts, cards]);

  const {
    data: unassingedCardData,
    loading: loadingUnassignedCard,
    refetch: refetchUnassignedCards,
  } = useQuery(GET_ASTRADA_EXTERNAL_PENDING_CARDS, {
    fetchPolicy: "cache-and-network",
    skip: connectionAccounts.length === 0,
  });

  const unassignedCards = useMemo<EnrolledCard[]>(() => {
    if (unassingedCardData?.pendingAstradaCardDetails) {
      return unassingedCardData?.pendingAstradaCardDetails.map((card: any) => ({
        id: card.id,
        cardNumber: card.cardNumber ?? "",
        nickname: card.nickname,
        status: "PENDING",
        // The API does not expose the issuer's cardholder name yet. See README.md.
        cardholderName: card.ownerName,
        assignedUser: null,
        enrollment: "unassigned",
        connectionId: card.account?.id ?? "",
        connectionName: card.account?.name ?? "",
        connectionNetwork: card.account?.astradaCard?.network ?? "",
        activatedAt: card.activePolicy?.card_activation_date ?? null,
        cardIssuerId: card.cardIssuerId,
      }));
    }
    return [];
  }, [unassingedCardData]);

  const [
    astradaAccount,
    { loading: loadingAstradaAccount, data: astradaAccountData },
  ] = useLazyQuery(LOAD_ASTRADA_ACCOUNT, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    astradaAccount();
  }, []);

  const astradaExternalAccount = useMemo(() => {
    if (astradaAccountData?.astradaAccount) {
      return astradaAccountData?.astradaAccount;
    }
    return {};
  }, [astradaAccountData]);

  const [requestBulkEnrollment, { loading: enrolling }] = useMutation(
    REQUEST_BULK_ENROLLMENT,
  );

  const [disconnectIntegration, { loading: disconnecting }] = useMutation(
    DISCONNECT_INTEGRATION,
  );

  const [updateAstradaConnection, { loading: updatingAstradaConnection }] =
    useMutation(UPDATE_ASTRADA_CONNECTION);

  const [connectIntegration, { loading: loadingConnection }] =
    useMutation(CONNECT_INTEGRATION);

  const [addAstradaExternalCards, { loading: addingAstradaCards }] =
    useMutation(ADD_ASTRADA_EXTERNAL_CARDS);
  /**
   * Refetches only the queries that are actually running.
   *
   * Apollo implements `skip` as `fetchPolicy: "standby"`, and `refetch` overrides that with
   * `network-only`, so refetching a skipped query silently starts it. Without these guards a
   * refetch would fire requests for cards and transactions before there is anything to fetch
   * them for, and would hit the network from mock mode.
   */
  const refetchAll = async () => {
    if (mocking) return;
    await refetchConnections();
    if (connectionAccounts.length > 0) await refetchCards();
    await refetchUnassignedCards();
  };

  const handleConnectIntegration = async (type: string, key: string) => {
    const response = await connectIntegration({
      variables: {
        type,
        key,
      },
    });
    await refetchAll();
    return response;
  };

  const handleUpdateAstradaConnection = async (
    accountId: string,
    enrollmentId: string,
    feedId: string,
  ) => {
    const response = await updateAstradaConnection({
      variables: {
        id: accountId,
        externalId: enrollmentId,
        feedId: feedId,
      },
    });
    await refetchAll();
    return response?.data;
  };

  const enrollProgram = async (networkReference: string) => {
    await requestBulkEnrollment({
      variables: {
        type: AccountType.ASTRADA_CONNECTION,
        networkReference: networkReference,
        id: astradaConnection?.id,
      },
    });
    await refetchAll();
  };

  const disconnectProgram = async (connectionId: string) => {
    await disconnectIntegration({ variables: { accountId: connectionId } });
    refetchAll();
  };

  const handleAssignAstradaCardUser = async (
    card: EnrolledCard,
    user: AssignedCardUser,
  ) => {
    await addAstradaExternalCards({
      variables: {
        accountId: card.connectionId,
        input: [
          {
            userId: user.id,
            cardId: card.id,
            cardIssuerId: card.cardIssuerId,
          },
        ],
      },
    });
    refetchAll();
  };

  /**
   * Local-only until the server can assign a user to an imported card. Absent
   * outside mock mode so the drawer disables its picker. See README.md.
   */
  const assignMockCardUser = async (
    card: EnrolledCard,
    user: AssignedCardUser,
  ) => {
    setMockCards((current) =>
      current.map((mockCard) =>
        mockCard.id === card.id
          ? {
              ...mockCard,
              status: CardStatusType.ACTIVE,
              assignedUser: user,
              enrollment: readCardEnrollment(CardStatusType.ACTIVE, user),
            }
          : mockCard,
      ),
    );
  };

  /** Local-only for the same reason; a card with no user lands in Unassigned. See README.md. */
  const reenrollMockCard = async (cardId: string) => {
    setMockCards((current) =>
      current.map((card) =>
        card.id === cardId
          ? {
              ...card,
              status: CardStatusType.ACTIVE,
              enrollment: readCardEnrollment(
                CardStatusType.ACTIVE,
                card.assignedUser,
              ),
            }
          : card,
      ),
    );
  };

  // Loading and error flags are cleared too, so the page renders its loaded state.
  // Mutations are deliberately left real. See README.md.
  if (mockMode !== "off") {
    const empty = mockMode === "empty";
    return {
      connections: empty ? [] : MOCK_CONNECTIONS,
      cards: empty ? [] : mockCards,
      assignCardUser: assignMockCardUser,
      reenrollCard: reenrollMockCard,
      connectionsLoading: false,
      connectionsError: undefined,
      cardsLoading: false,
      enrolling,
      disconnecting,
      astradaConnection,
      setAstradaConnection,
      enrollProgram,
      disconnectProgram,
      refetchAll,
      astradaExternalAccount,
      loadingAstradaAccount,
      handleUpdateAstradaConnection,
      updatingAstradaConnection,
      handleConnectIntegration,
      loadingConnection,
      refetchUnassignedCards,
      unassignedCards,
      loadingUnassignedCard,
      addingAstradaCards,
    };
  }

  return {
    connections,
    cards,
    assignCardUser: handleAssignAstradaCardUser,
    reenrollCard: undefined,
    connectionsLoading,
    connectionsError,
    cardsLoading,
    enrolling,
    disconnecting,
    astradaConnection,
    setAstradaConnection,
    enrollProgram,
    disconnectProgram,
    refetchAll,
    astradaExternalAccount,
    loadingAstradaAccount,
    handleUpdateAstradaConnection,
    updatingAstradaConnection,
    handleConnectIntegration,
    loadingConnection,
    refetchUnassignedCards,
    loadingUnassignedCard,
    unassignedCards,
    addingAstradaCards,
  };
}

export default useAstradaLinkedAccounts;
